import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../common/Button";
import { useTranslation } from "react-i18next";
import logo from "../../assets/common/logo.svg";
import logoIcon from "../../assets/common/logo-icon.svg";
import loginBg from "../../assets/login/login-bg.png";
import {
  initializeMsal,
  getValidToken,
  msalInstance,
} from "../../utils/tokenManager";

const Login = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState("");
  const { t } = useTranslation();

  const processLoginSuccess = useCallback(
    async (loginResponse) => {
      try {
        msalInstance.setActiveAccount(loginResponse.account);

        // Get token (don’t store it)
        const accessToken = await getValidToken();
        const email = loginResponse.account.username;

        if (!email) throw new Error("Email not found in Azure AD response.");

        // Call backend API to validate user & get role
        const response = await fetch(
          `${process.env.REACT_APP_APIBASEURL}/api/login`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`, // ✅ transient token
            },
            body: JSON.stringify({ email }),
          },
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to retrieve API token.");
        }

        const responseData = await response.json();
        const { role, fullName, clientName, clinentId, userId } =
          responseData.data;

        // ✅ only store non-sensitive metadata
        sessionStorage.setItem("userRole", role);
        sessionStorage.setItem("userName", fullName);
        sessionStorage.setItem("clientName", clientName);
        sessionStorage.setItem("clinentId", clinentId);
        sessionStorage.setItem("agentId", userId);

        if (role === "Admin") navigate("/dashboard");
        else if (role === "Agent") navigate("/agent");
        else if (role === "Client") navigate("/client");
      } catch (error) {
        console.error("Processing Login Error:", error);
        setAuthError(t("loginError"));
      } finally {
        setIsLoading(false);
      }
    },
    [navigate, t],
  );

  useEffect(() => {
    const init = async () => {
      try {
        await initializeMsal();
        const response = await msalInstance.handleRedirectPromise();

        // Either we got a response from the promise, or the app was already initialized
        // and fixed the active account.
        const account = response?.account || msalInstance.getActiveAccount();
        const userRole = sessionStorage.getItem("userRole");

        if (account && !userRole) {
          // We have an Azure account but haven't finished backend login
          setIsLoading(true);
          await processLoginSuccess(response || { account });
        } else if (account && userRole) {
          // Already fully logged in, redirect to dashboard
          if (userRole === "Admin") navigate("/dashboard");
          else if (userRole === "Agent") navigate("/agent");
          else if (userRole === "Client") navigate("/client");
        }
      } catch (error) {
        console.error("MSAL Initialization Error:", error);
      }
    };
    init();
  }, [navigate, processLoginSuccess]);

  const handleAzureLogin = async () => {
    if (isLoading) return;
    setIsLoading(true);
    setAuthError("");

    const loginRequest = {
      scopes: [`api://${process.env.REACT_APP_CLIENT_ID}/apilogin`],
    };

    try {
      // Detect if user is on mobile or iPad (including iPadOS desktop mode)
      const isMobile =
        /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

      if (isMobile) {
        // Use redirect for mobile to avoid popup blockers
        await msalInstance.loginRedirect(loginRequest);
      } else {
        // Use popup for desktop for better UX
        const loginResponse = await msalInstance.loginPopup(loginRequest);
        await processLoginSuccess(loginResponse);
      }
    } catch (error) {
      console.error("Login Error:", error);
      setAuthError(t("loginError"));
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 sm:p-6"
      style={{
        background:
          "linear-gradient(-80deg, #051D42 20%, #0C469F 82%, #0D4AA8 100%)",
      }}
    >
      <div className="w-full max-w-[1200px] h-auto min-h-[600px] flex flex-col lg:flex-row rounded-xl overflow-hidden mt-14 md:mt-0">
        <div
          className="w-full lg:w-1/2 h-[300px] lg:h-auto relative bg-cover bg-center bg-no-repeat rounded-[20px]"
          style={{ backgroundImage: `url(${loginBg})` }}
        >
          <div className="absolute inset-0 flex flex-col justify-between p-6 sm:p-8 lg:p-12">
            <div>
              <img src={logo} alt="Kikonnect-logo" className="h-6 sm:h-8" />
            </div>
            <div className="text-white text-center">
              <h1 className="text-2xl mb-2 font-bold">
                {t("loginText.place")}
              </h1>
              <p className="text-2xl">{t("loginText.connects")}</p>
            </div>
          </div>
        </div>
        <div className="w-full lg:w-[480px] p-6 sm:p-8 lg:p-12 flex flex-col">
          <img
            src={logoIcon}
            alt=""
            className="h-[70px] md:h-[80px] w-[60px] md:w-[70px] mb-5"
          />
          <div className="flex items-center mb-5">
            <h1 className="text-4xl md:text-7xl text-white">{t("start")}</h1>
          </div>
          <div className="border-b border-gray-300 pb-2">
            <p className="text-gray-400 text-xl">{t("welcome")} Kikonnect</p>
          </div>
          <div className="flex-1 flex flex-col">
            {authError && (
              <div className="mb-4 text-red-500 text-sm">{authError}</div>
            )}
            <div className="mt-[65px] w-full">
              <Button
                variant="primary"
                onClick={handleAzureLogin}
                isLoading={isLoading}
                className="w-full"
              >
                {t("button.login")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
